/**
 * Damage Claims — R6.14 worked form.
 * Single-page form. Submission adds to Track with "in-review" status.
 * Atlas: Services > Damage Claims.
 */
"use client";

import { useState } from "react";
import Link from "next/link";
import { AlertTriangle, Upload, CheckCircle2, Shield } from "lucide-react";
import { useActivePersona } from "@/lib/persona";
import { useT } from "@/lib/locale";
import { useDemoSessionStore } from "@petci/shared-ui";

export default function DamageClaimsPage() {
  const persona = useActivePersona();
  const t = useT();
  const addRequest = useDemoSessionStore((s) => s.addRequest);

  const [description, setDescription] = useState("");
  const [docUploaded, setDocUploaded] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [claimNumber] = useState(() => {
    const num = Math.floor(10000 + Math.random() * 90000);
    return `PEL-DC-${num}`;
  });

  function handleSubmit() {
    const decisionDate = new Date("2026-05-10");
    decisionDate.setDate(decisionDate.getDate() + 21);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const expectedDecision = decisionDate.toISOString().split("T")[0]!;

    addRequest(persona.id, {
      id: claimNumber,
      type: "Damage Claim",
      status: {
        state: "in-review",
        receivedAt: "2026-05-10",
        expectedDecision,
      },
      summary: description || "Damage claim submitted",
    });
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto space-y-6">
        <div className="card-surface text-center space-y-4 py-6">
          <div className="w-16 h-16 rounded-full bg-semantic-success/10 flex items-center justify-center mx-auto">
            <CheckCircle2 size={32} className="text-semantic-success-text" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-display font-bold text-neutral-900">
              {t("services.dcClaimSubmitted")}
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              {t("services.dcReceivedMsg")}
            </p>
          </div>
          <div className="rounded-lg bg-neutral-50 p-4 space-y-1">
            <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">
              {t("services.dcClaimReference")}
            </p>
            <p className="text-lg font-mono font-bold text-brand-primary-text">
              {claimNumber}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link href="/services/track" className="btn-primary w-full justify-center">
              {t("services.trackTitle")}
            </Link>
            <Link href="/services" className="btn-secondary w-full justify-center">
              {t("services.backToServices")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <Link
        href="/services"
        className="inline-flex items-center gap-1 text-sm text-brand-primary-text hover:underline py-1 -my-1"
      >
        ← {t("services.allServices")}
      </Link>

      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg bg-semantic-warning/10 flex items-center justify-center">
          <AlertTriangle size={20} className="text-semantic-warning-text" aria-hidden />
        </span>
        <div>
          <h1 className="text-xl font-display font-bold text-neutral-900">
            {t("services.dcTitle")}
          </h1>
          <p className="text-xs text-neutral-500">
            {t("services.dcIntro")}
          </p>
        </div>
      </div>

      <div className="card-surface space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-neutral-700">{t("services.dcOutageDate")}</span>
          <input
            type="date"
            defaultValue="2026-05-03"
            className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
          />
        </label>

        <div>
          <span className="text-sm font-medium text-neutral-700">{t("services.serviceAddress")}</span>
          <p className="mt-1 text-sm text-neutral-500 bg-neutral-50 rounded-lg px-3 py-2">
            {persona.serviceAddress.line1}, {persona.serviceAddress.district}
          </p>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-neutral-700">
            {t("services.dcDescribeLabel")}
          </span>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-500 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
            placeholder={t("services.dcDescribePlaceholder")}
          />
        </label>

        <div>
          <span className="text-sm font-medium text-neutral-700">{t("services.dcAttachQuotes")}</span>
          {docUploaded ? (
            <div className="mt-1 rounded-lg border border-semantic-success/30 bg-semantic-success/5 p-3 flex items-center gap-3">
              <CheckCircle2 size={18} className="text-semantic-success-text" aria-hidden />
              <div>
                <p className="text-sm font-medium text-neutral-900">{t("services.dcQuoteFilename")}</p>
                <p className="text-xs text-neutral-500">{t("services.dcQuoteSize")}</p>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setDocUploaded(true)}
              className="mt-1 w-full rounded-lg border-2 border-dashed border-neutral-300 p-4 text-center hover:border-brand-primary hover:bg-brand-primary/5 transition-colors duration-fast"
            >
              <Upload size={20} className="text-neutral-500 mx-auto mb-1" aria-hidden />
              <p className="text-xs text-neutral-500">{t("services.dcAttachPrompt")}</p>
            </button>
          )}
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={authorized}
            onChange={(e) => setAuthorized(e.target.checked)}
            className="mt-0.5 rounded border-neutral-300 text-brand-primary-text focus:ring-brand-primary"
          />
          <span className="text-xs text-neutral-700 leading-relaxed">
            {t("services.dcAuthorize")}
          </span>
        </label>
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!authorized || !description}
        className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Shield size={16} aria-hidden />
        {t("services.dcSubmit")}
      </button>

      <p className="text-xs text-neutral-500 text-center">
        {t("services.dcNeedHelp")}{" "}
        <a
          href="https://pelicanenergytci.com/power-outages-damage-claims/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary-text hover:underline"
        >
          {t("services.dcViewProcess")}
        </a>
      </p>
    </div>
  );
}
